import React, { useState, useMemo, useEffect, useRef } from 'react';
import html2canvas from 'html2canvas';
import axiosInstance from '../utils/axiosConfig';

const LandCostModal = ({ isOpen, onClose, onApply, fileId, isNewFile = false, initialCostData = null }) => {
    const initialData = {
        shipment_term: '',
        weight: '',
        goods_cost: '',
        exchange_rate: '',
        insurance: '',
        mashatih_num: '',
        car_num: '',
        storage_days_num: '',
        purchases_tax_value: '',
        customs_value: '',
        d_q_m_value: '',
        office_fee_quantity: '',
        office_fee_value: '',
        diff_clearance_fee_quantity: '',
        diff_clearance_fee_value: '',
        special_clearance_fee_quantity: '',
        special_clearance_fee_value: '',
        car_crossing_fee_value: '',
        car_weighing_value: '',
        dow_ins_mashatih_value: '',
        storage_mashatih_day_value: '',
        diff_cost_quantity: '',
        diff_cost_value: '',
        prev_balance_quantity: '',
        prev_balance_value: ''
    };
    
    const [data, setData] = useState(initialData);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [hasExistingData, setHasExistingData] = useState(false);
    const [error, setError] = useState('');
    const [validationErrors, setValidationErrors] = useState({});
    const [exporting, setExporting] = useState(false);
    const modalRef = useRef(null);

    useEffect(() => {
        if (!isOpen) return;
        
        if (isNewFile) {
            if (initialCostData) {
                const loadedData = {};
                Object.keys(initialData).forEach(key => {
                    if (initialCostData[key] !== undefined && initialCostData[key] !== null) {
                        loadedData[key] = String(initialCostData[key]);
                    } else {
                        loadedData[key] = '';
                    }
                });
                setData(loadedData);
                setHasExistingData(true);
            } else {
                setData(initialData);
                setHasExistingData(false);
            }
        } else if (fileId) {
            loadExistingData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, fileId, isNewFile, initialCostData]);

    const loadExistingData = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get(`/land-cost/${fileId}/`);
            if (response.data && response.data.exists !== false) {
                const loadedData = {};
                Object.keys(initialData).forEach(key => {
                    if (response.data[key] !== undefined && response.data[key] !== null) {
                        loadedData[key] = String(response.data[key]);
                    } else {
                        loadedData[key] = '';
                    }
                });
                setData(loadedData);
                setHasExistingData(true);
            } else {
                setData(initialData);
                setHasExistingData(false);
            }
        } catch (error) {
            console.error('Error loading cost data:', error);
            setData(initialData);
            setHasExistingData(false);
        }
        setLoading(false);
    };

    const handleSaveAndApply = async () => {
        setSaving(true);
        setError('');
        try {
            const apiData = {};
            Object.keys(data).forEach(key => {
                if (key === 'shipment_term') {
                    apiData[key] = data[key] || '';
                } else {
                    apiData[key] = parseFloat(data[key]) || 0;
                }
            });

            if (fileId && !isNewFile) {
                await axiosInstance.post(`/land-cost/${fileId}/`, apiData);
            }
            
            onApply(costs.total_payment, apiData);
        } catch (error) {
            console.error('Error saving cost data:', error);
            setError('حدث خطأ أثناء حفظ البيانات');
        }
        setSaving(false);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        
        if (validationErrors[name]) {
            setValidationErrors(prev => ({ ...prev, [name]: false }));
        }
        if (error) setError('');
        
        if (name === 'shipment_term') {
            setData(prev => ({ ...prev, [name]: value }));
            return;
        }
        
        let cleanValue = value.replace(/[^\d.]/g, '');
        const parts = cleanValue.split('.');
        if (parts.length > 2) {
            cleanValue = parts[0] + '.' + parts.slice(1).join('');
        }
        
        setData(prev => ({ ...prev, [name]: cleanValue }));
    };

    const costs = useMemo(() => {
        const p = (v) => parseFloat(v) || 0;
        const goods_total = p(data.goods_cost) * p(data.exchange_rate);
        const car_crossing_total = p(data.car_num) * p(data.car_crossing_fee_value);
        const crossing_fee = (p(data.mashatih_num) * 37) + (p(data.car_num) * 237);
        const statement_value = goods_total + crossing_fee + p(data.insurance);
        const purchases_tax_total = p(data.purchases_tax_value) * p(data.weight);
        const customs_total = p(data.customs_value) * statement_value;
        const d_q_m_total = (statement_value + customs_total + purchases_tax_total) * p(data.d_q_m_value);
        const office_fee_total = p(data.office_fee_quantity) * p(data.office_fee_value);
        const diff_clearance_total = p(data.diff_clearance_fee_quantity) * p(data.diff_clearance_fee_value);
        const special_clearance_total = p(data.special_clearance_fee_quantity) * p(data.special_clearance_fee_value);
        const car_weighing_total = p(data.car_num) * p(data.car_weighing_value);
        const dow_ins_mashatih_total = p(data.mashatih_num) * p(data.dow_ins_mashatih_value);
        const storage_mashatih_total = p(data.storage_days_num) * p(data.mashatih_num) * p(data.storage_mashatih_day_value);
        const diff_cost_total = p(data.diff_cost_quantity) * p(data.diff_cost_value);
        const prev_balance_total = p(data.prev_balance_quantity) * p(data.prev_balance_value);
        const non_taxable_total = d_q_m_total + office_fee_total + car_crossing_total + prev_balance_total;
        const taxable_total = diff_clearance_total + special_clearance_total + car_weighing_total + 
            dow_ins_mashatih_total + storage_mashatih_total + diff_cost_total;
        const taxable_vat = 0.18 * taxable_total;
        const total_payment = non_taxable_total + taxable_total + taxable_vat;

        return {
            goods_total, crossing_fee, statement_value, purchases_tax_total, customs_total,
            d_q_m_total, office_fee_total, diff_clearance_total, special_clearance_total,
            car_crossing_total, car_weighing_total, dow_ins_mashatih_total, storage_mashatih_total,
            diff_cost_total, prev_balance_total, non_taxable_total, taxable_total, taxable_vat, total_payment
        };
    }, [data]);

    const handleExportImage = async () => {
        if (!modalRef.current) return;
        setExporting(true);
        try {
            const original = modalRef.current;
            const clone = original.cloneNode(true);

            const header = clone.querySelector('.modal-header');
            if (header) header.remove();

            const buttonsSection = clone.querySelector('[data-export-hide]');
            if (buttonsSection) buttonsSection.remove();

            const errorDiv = clone.querySelector('[style*="fff5f5"]');
            if (errorDiv) errorDiv.remove();

            // Remove all children except the content div (which is after modal-header)
            const allChildren = Array.from(clone.children);
            allChildren.forEach(child => {
                if (child.classList && child.classList.contains('modal-header')) {
                    child.remove();
                } else {
                    child.style.height = 'auto';
                    child.style.maxHeight = 'none';
                    child.style.overflow = 'visible';
                    child.style.padding = '10px';
                    child.style.paddingBottom = '5px';
                }
            });

            const origInputs = original.querySelectorAll('input, textarea, select');
            const cloneInputs = clone.querySelectorAll('input, textarea, select');
            origInputs.forEach((orig, i) => {
                if (cloneInputs[i]) {
                    cloneInputs[i].setAttribute('value', orig.value);
                    cloneInputs[i].value = orig.value;
                }
            });

            clone.querySelectorAll('*').forEach(el => {
                el.style.transition = 'none';
                el.style.animation = 'none';
            });

            // Remove extra space from all containers
            clone.querySelectorAll('div, table, tbody, tr, td').forEach(el => {
                el.style.marginBottom = '0';
            });

            Object.assign(clone.style, {
                position: 'absolute',
                top: '0',
                left: '-9999px',
                height: 'auto',
                maxHeight: 'none',
                overflow: 'visible',
                width: original.offsetWidth + 'px',
                zIndex: '-1',
                background: '#ffffff',
                opacity: '1',
                transform: 'none',
                animation: 'none',
                transition: 'none',
                borderRadius: '0',
                padding: '0',
            });

            document.body.appendChild(clone);
            await new Promise(resolve => setTimeout(resolve, 150));

            const canvas = await html2canvas(clone, {
                scale: 2,
                useCORS: true,
                backgroundColor: '#ffffff',
                logging: false,
            });

            document.body.removeChild(clone);

            const link = document.createElement('a');
            link.download = `land_cost_${fileId || 'new'}_${new Date().toISOString().slice(0,10)}.png`;
            link.href = canvas.toDataURL('image/png');
            link.click();
        } catch (err) {
            console.error('Export error:', err);
            setError('حدث خطأ أثناء تصدير الصورة');
        }
        setExporting(false);
    };

    if (!isOpen) return null;

    const cellStyle = { border: '1px solid #ccc', padding: '4px 8px', textAlign: 'center', fontSize: '11px', height: '24px' };
    const numberCellStyle = { ...cellStyle, direction: 'ltr' };
    const headerCellStyle = { ...cellStyle, background: '#03428d', color: 'white', fontWeight: '600' };
    const inputCellStyle = { ...cellStyle, padding: '0', background: '#fff' };
    const inputStyle = { width: '100%', height: '100%', border: 'none', textAlign: 'center', fontSize: '11px', background: 'transparent', outline: 'none' };
    const disabledCellStyle = { ...cellStyle, background: '#e8f4fc', color: '#03428d', fontWeight: '600' };
    const yellowCell = { ...cellStyle, background: '#f8f9fa', textAlign: 'right', paddingRight: '8px', fontSize: '10px', color: '#333', fontWeight: '500' };
    const greenCell = { ...cellStyle, background: '#e8f4fc', color: '#03428d', fontWeight: '600' };
    const summaryLabelStyle = { ...cellStyle, background: '#f8f9fa', color: '#333', textAlign: 'right', paddingRight: '8px', fontWeight: '600' };

    return (
        <div className="modal-overlay" onClick={onClose} style={{ zIndex: 2100 }}>
            <div ref={modalRef} className="modal cost-calculation-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '1400px', width: '98vw', height: '98vh', maxHeight: '98vh', padding: 0, overflow: 'hidden', borderRadius: '12px' }}>
                <div className="modal-header" style={{ background: '#03428d', padding: '12px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderRadius: '12px 12px 0 0' }}>
                    <h3 style={{ color: 'white', margin: 0, fontSize: '16px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <svg style={{ width: '20px', height: '20px' }} fill="currentColor" viewBox="0 0 20 20">
                            <path d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7.414A2 2 0 0015.414 6L12 2.586A2 2 0 0010.586 2H6zm2 10a1 1 0 10-2 0v3a1 1 0 102 0v-3zm2-3a1 1 0 011 1v5a1 1 0 11-2 0v-5a1 1 0 011-1zm4 2a1 1 0 10-2 0v3a1 1 0 102 0v-3z" />
                        </svg>
                        {hasExistingData ? 'عرض/تعديل حسبة ملف بري (LAND)' : 'حساب تكلفة ملف بري (LAND)'}
                    </h3>
                    <button type="button" onClick={onClose} style={{ background: 'transparent', border: 'none', color: 'white', cursor: 'pointer', padding: '5px' }}>
                        <svg fill="currentColor" viewBox="0 0 20 20" style={{ width: '20px', height: '20px' }}>
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                    </button>
                </div>
                
                {loading ? (
                    <div style={{ padding: '3rem', textAlign: 'center' }}>
                        <div style={{ width: '50px', height: '50px', border: '4px solid #e1e8ed', borderTop: '4px solid #03428d', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 1rem' }}></div>
                        <p style={{ color: '#64748b' }}>جاري تحميل البيانات...</p>
                        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
                    </div>
                ) : (
                    <div style={{ padding: '15px', overflow: 'auto', height: 'calc(98vh - 60px)', maxHeight: 'calc(98vh - 60px)' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', direction: 'rtl', tableLayout: 'fixed' }}>
                            <colgroup>
                                <col style={{ width: '8%' }} />
                                <col style={{ width: '12%' }} />
                                <col style={{ width: '10%' }} />
                                <col style={{ width: '12%' }} />
                                <col style={{ width: '10%' }} />
                                <col style={{ width: '10%' }} />
                                <col style={{ width: '12%' }} />
                                <col style={{ width: '10%' }} />
                                <col style={{ width: '16%' }} />
                            </colgroup>
                            <thead>
                                <tr>
                                    <td style={headerCellStyle}>رقم الملف</td>
                                    <td style={headerCellStyle}>شروط الشحن</td>
                                    <td style={headerCellStyle}>الوزن الصافي</td>
                                    <td style={headerCellStyle}>قيمة البضاعة</td>
                                    <td style={headerCellStyle}>سعر الصرف</td>
                                    <td style={headerCellStyle}>المجموع</td>
                                    <td style={headerCellStyle}>رسوم معبر</td>
                                    <td style={headerCellStyle}>تأمين</td>
                                    <td style={headerCellStyle}>قيمة البيان</td>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td style={cellStyle}>{fileId || '-'}</td>
                                    <td style={inputCellStyle}><input type="text" name="shipment_term" value={data.shipment_term} onChange={handleChange} style={inputStyle} autoComplete="off" /></td>
                                    <td style={inputCellStyle}><input type="text" inputMode="decimal" name="weight" value={data.weight} onChange={handleChange} style={inputStyle} autoComplete="off" /></td>
                                    <td style={inputCellStyle}><input type="text" inputMode="decimal" name="goods_cost" value={data.goods_cost} onChange={handleChange} style={inputStyle} autoComplete="off" /></td>
                                    <td style={inputCellStyle}><input type="text" inputMode="decimal" name="exchange_rate" value={data.exchange_rate} onChange={handleChange} style={inputStyle} autoComplete="off" /></td>
                                    <td style={disabledCellStyle}>{Math.round(costs.goods_total)}</td>
                                    <td style={disabledCellStyle}>{Math.round(costs.crossing_fee)}</td>
                                    <td style={inputCellStyle}><input type="text" inputMode="decimal" name="insurance" value={data.insurance} onChange={handleChange} style={inputStyle} autoComplete="off" /></td>
                                    <td style={disabledCellStyle}>{Math.round(costs.statement_value)}</td>
                                </tr>
                            </tbody>
                        </table>

                        <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', marginTop: '8px', direction: 'rtl', gap: '15px' }}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <div style={{ ...cellStyle, width: '80px', textAlign: 'center' }}>عدد المشاتيح</div>
                                <div style={{ ...inputCellStyle, width: '60px' }}><input type="text" inputMode="numeric" name="mashatih_num" value={data.mashatih_num} onChange={handleChange} style={inputStyle} autoComplete="off" /></div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <div style={{ ...cellStyle, width: '80px', textAlign: 'center' }}>عدد السيارات</div>
                                <div style={{ ...inputCellStyle, width: '60px' }}><input type="text" inputMode="numeric" name="car_num" value={data.car_num} onChange={handleChange} style={inputStyle} autoComplete="off" /></div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <div style={{ ...cellStyle, width: '100px', textAlign: 'center' }}>عدد أيام التخزين</div>
                                <div style={{ ...inputCellStyle, width: '60px' }}><input type="text" inputMode="numeric" name="storage_days_num" value={data.storage_days_num} onChange={handleChange} style={inputStyle} autoComplete="off" /></div>
                            </div>
                        </div>

                        <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0', direction: 'rtl', marginTop: '10px', tableLayout: 'fixed' }}>
                            <colgroup>
                                <col style={{ width: '3%' }} />
                                <col style={{ width: '22%' }} />
                                <col style={{ width: '6%' }} />
                                <col style={{ width: '6%' }} />
                                <col style={{ width: '7%' }} />
                                <col style={{ width: '2%' }} />
                                <col style={{ width: '54%' }} />
                            </colgroup>
                            <thead>
                                <tr>
                                    <td style={headerCellStyle}></td>
                                    <td style={headerCellStyle}>التفاصيل</td>
                                    <td style={headerCellStyle}>الكمية</td>
                                    <td style={headerCellStyle}>القيمة</td>
                                    <td style={headerCellStyle}>المجموع</td>
                                    <td style={{ border: 'none', background: 'transparent' }}></td>
                                    <td style={{ ...headerCellStyle, background: '#03428d', color: '#fff' }}>شروط المطالبة المالية</td>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td style={numberCellStyle}>1</td>
                                    <td style={cellStyle}>ضريبة مشتريات</td>
                                    <td style={disabledCellStyle}></td>
                                    <td style={inputCellStyle}><input type="text" inputMode="decimal" name="purchases_tax_value" value={data.purchases_tax_value} onChange={handleChange} style={inputStyle} autoComplete="off" /></td>
                                    <td style={disabledCellStyle}>{Math.round(costs.purchases_tax_total)}</td>
                                    <td style={{ border: 'none', background: 'transparent' }}></td>
                                    <td style={yellowCell}>المطالبة المالية هي عبارة عن حسبة تقريبية وليست نهائية قابلة للزيادة او النقصان.</td>
                                </tr>
                                <tr>
                                    <td style={numberCellStyle}>2</td>
                                    <td style={cellStyle}>جمارك</td>
                                    <td style={disabledCellStyle}></td>
                                    <td style={inputCellStyle}><input type="text" inputMode="decimal" name="customs_value" value={data.customs_value} onChange={handleChange} style={inputStyle} autoComplete="off" /></td>
                                    <td style={disabledCellStyle}>{Math.round(costs.customs_total)}</td>
                                    <td style={{ border: 'none', background: 'transparent' }}></td>
                                    <td style={yellowCell}>المطالبة المالية غير شاملة أرضيات داخل الميناء او ارضيات مخازن خارج الميناء.</td>
                                </tr>
                                <tr>
                                    <td style={numberCellStyle}>3</td>
                                    <td style={cellStyle}>ض.ق.م</td>
                                    <td style={disabledCellStyle}></td>
                                    <td style={inputCellStyle}><input type="text" inputMode="decimal" name="d_q_m_value" value={data.d_q_m_value} onChange={handleChange} style={inputStyle} autoComplete="off" /></td>
                                    <td style={disabledCellStyle}>{Math.round(costs.d_q_m_total)}</td>
                                    <td style={{ border: 'none', background: 'transparent' }}></td>
                                    <td style={yellowCell}>المطالبة المالية غير شاملة اعطال حاوية (ما يزيد عن فترة السماح).</td>
                                </tr>
                                <tr>
                                    <td style={numberCellStyle}>4</td>
                                    <td style={cellStyle}>اتعاب نصار</td>
                                    <td style={inputCellStyle}><input type="text" inputMode="numeric" name="office_fee_quantity" value={data.office_fee_quantity} onChange={handleChange} style={inputStyle} autoComplete="off" /></td>
                                    <td style={inputCellStyle}><input type="text" inputMode="decimal" name="office_fee_value" value={data.office_fee_value} onChange={handleChange} style={inputStyle} autoComplete="off" /></td>
                                    <td style={disabledCellStyle}>{Math.round(costs.office_fee_total)}</td>
                                    <td style={{ border: 'none', background: 'transparent' }}></td>
                                    <td style={yellowCell}>المطالبة المالية غير شاملة غرامات جمركية او إعادة تقييم لقيمة الفاتورة.</td>
                                </tr>
                                <tr>
                                    <td style={numberCellStyle}>5</td>
                                    <td style={cellStyle}>متفرقات تخليص (حسب الفواتير الصادرة)</td>
                                    <td style={inputCellStyle}><input type="text" inputMode="numeric" name="diff_clearance_fee_quantity" value={data.diff_clearance_fee_quantity} onChange={handleChange} style={inputStyle} autoComplete="off" /></td>
                                    <td style={inputCellStyle}><input type="text" inputMode="decimal" name="diff_clearance_fee_value" value={data.diff_clearance_fee_value} onChange={handleChange} style={inputStyle} autoComplete="off" /></td>
                                    <td style={disabledCellStyle}>{Math.round(costs.diff_clearance_total)}</td>
                                    <td style={{ border: 'none', background: 'transparent' }}></td>
                                    <td style={yellowCell}>المطالبة المالية غير شاملة تفتيش الحاوية تفتيش رملية او مخزن بأمر جمركي شامل ودقيق (عتالة).</td>
                                </tr>
                                <tr>
                                    <td style={numberCellStyle}>6</td>
                                    <td style={cellStyle}>اتعاب خاصة (صحة\اتصالات\تيكن)</td>
                                    <td style={inputCellStyle}><input type="text" inputMode="numeric" name="special_clearance_fee_quantity" value={data.special_clearance_fee_quantity} onChange={handleChange} style={inputStyle} autoComplete="off" /></td>
                                    <td style={inputCellStyle}><input type="text" inputMode="decimal" name="special_clearance_fee_value" value={data.special_clearance_fee_value} onChange={handleChange} style={inputStyle} autoComplete="off" /></td>
                                    <td style={disabledCellStyle}>{Math.round(costs.special_clearance_total)}</td>
                                    <td style={{ border: 'none', background: 'transparent' }}></td>
                                    <td style={yellowCell}>المطالبة المالية غير شاملة أي عملية اتلاف او نقل البضاعة الى المخازن الجمركية وتخزينها.</td>
                                </tr>
                                <tr>
                                    <td style={numberCellStyle}>7</td>
                                    <td style={cellStyle}>رسوم معبر لكل سيارة</td>
                                    <td style={disabledCellStyle}>{data.car_num || 0}</td>
                                    <td style={inputCellStyle}><input type="text" inputMode="decimal" name="car_crossing_fee_value" value={data.car_crossing_fee_value} onChange={handleChange} style={inputStyle} autoComplete="off" /></td>
                                    <td style={disabledCellStyle}>{Math.round(costs.car_crossing_total)}</td>
                                    <td style={{ border: 'none', background: 'transparent' }}></td>
                                    <td style={yellowCell}>المطالبة المالية غير شاملة نقل الى مخزن الزبون.</td>
                                </tr>
                                <tr>
                                    <td style={numberCellStyle}>8</td>
                                    <td style={cellStyle}>توزين سيارات</td>
                                    <td style={disabledCellStyle}>{data.car_num || 0}</td>
                                    <td style={inputCellStyle}><input type="text" inputMode="decimal" name="car_weighing_value" value={data.car_weighing_value} onChange={handleChange} style={inputStyle} autoComplete="off" /></td>
                                    <td style={disabledCellStyle}>{Math.round(costs.car_weighing_total)}</td>
                                    <td style={{ border: 'none', background: 'transparent' }}></td>
                                    <td style={{ ...cellStyle, background: 'transparent', border: 'none' }}></td>
                                </tr>
                                <tr>
                                    <td style={numberCellStyle}>9</td>
                                    <td style={cellStyle}>تنزيل وتحميل مشاتيح</td>
                                    <td style={disabledCellStyle}>{data.mashatih_num || 0}</td>
                                    <td style={inputCellStyle}><input type="text" inputMode="decimal" name="dow_ins_mashatih_value" value={data.dow_ins_mashatih_value} onChange={handleChange} style={inputStyle} autoComplete="off" /></td>
                                    <td style={disabledCellStyle}>{Math.round(costs.dow_ins_mashatih_total)}</td>
                                    <td style={{ border: 'none', background: 'transparent' }}></td>
                                    <td style={{ ...cellStyle, background: 'transparent', border: 'none' }}></td>
                                </tr>
                                <tr>
                                    <td style={numberCellStyle}>10</td>
                                    <td style={cellStyle}>تخزين مشاتيح لكل يوم بعد تحرير البضاعة</td>
                                    <td style={disabledCellStyle}>{data.mashatih_num || 0}</td>
                                    <td style={inputCellStyle}><input type="text" inputMode="decimal" name="storage_mashatih_day_value" value={data.storage_mashatih_day_value} onChange={handleChange} style={inputStyle} autoComplete="off" /></td>
                                    <td style={disabledCellStyle}>{Math.round(costs.storage_mashatih_total)}</td>
                                    <td style={{ border: 'none', background: 'transparent' }}></td>
                                    <td style={{ ...cellStyle, background: 'transparent', border: 'none' }}></td>
                                </tr>
                                <tr>
                                    <td style={numberCellStyle}>11</td>
                                    <td style={cellStyle}>إعادة تقييم / غرامات / تفتيش / حجز</td>
                                    <td style={inputCellStyle}><input type="text" inputMode="numeric" name="diff_cost_quantity" value={data.diff_cost_quantity} onChange={handleChange} style={inputStyle} autoComplete="off" /></td>
                                    <td style={inputCellStyle}><input type="text" inputMode="decimal" name="diff_cost_value" value={data.diff_cost_value} onChange={handleChange} style={inputStyle} autoComplete="off" /></td>
                                    <td style={disabledCellStyle}>{Math.round(costs.diff_cost_total)}</td>
                                    <td style={{ border: 'none', background: 'transparent' }}></td>
                                    <td style={{ ...cellStyle, background: 'transparent', border: 'none' }}></td>
                                </tr>
                                <tr>
                                    <td style={numberCellStyle}>12</td>
                                    <td style={cellStyle}>رصيد سابق</td>
                                    <td style={inputCellStyle}><input type="text" inputMode="numeric" name="prev_balance_quantity" value={data.prev_balance_quantity} onChange={handleChange} style={inputStyle} autoComplete="off" /></td>
                                    <td style={inputCellStyle}><input type="text" inputMode="decimal" name="prev_balance_value" value={data.prev_balance_value} onChange={handleChange} style={inputStyle} autoComplete="off" /></td>
                                    <td style={disabledCellStyle}>{Math.round(costs.prev_balance_total)}</td>
                                    <td style={{ border: 'none', background: 'transparent' }}></td>
                                    <td style={{ ...cellStyle, background: 'transparent', border: 'none' }}></td>
                                </tr>
                                <tr>
                                    <td colSpan="4" style={summaryLabelStyle}>المجموع الغير خاضع للضريبة</td>
                                    <td style={greenCell}>{Math.round(costs.non_taxable_total)}</td>
                                    <td style={{ border: 'none', background: 'transparent' }}></td>
                                    <td style={{ ...cellStyle, background: 'transparent', border: 'none' }}></td>
                                </tr>
                                <tr>
                                    <td colSpan="4" style={summaryLabelStyle}>المجموع الخاضع للضريبة</td>
                                    <td style={greenCell}>{Math.round(costs.taxable_total)}</td>
                                    <td style={{ border: 'none', background: 'transparent' }}></td>
                                    <td style={{ ...cellStyle, background: 'transparent', border: 'none' }}></td>
                                </tr>
                                <tr>
                                    <td colSpan="3" style={summaryLabelStyle}>ض.ق.م</td>
                                    <td style={greenCell}>0.18</td>
                                    <td style={greenCell}>{Math.round(costs.taxable_vat)}</td>
                                    <td style={{ border: 'none', background: 'transparent' }}></td>
                                    <td style={{ ...cellStyle, background: 'transparent', border: 'none' }}></td>
                                </tr>
                                <tr>
                                    <td colSpan="4" style={{ ...summaryLabelStyle }}>المجموع للدفع</td>
                                    <td style={{ ...greenCell, fontSize: '12px' }}>{Math.floor(costs.total_payment || 0)}</td>
                                    <td style={{ border: 'none', background: 'transparent' }}></td>
                                    <td style={{ ...cellStyle, background: 'transparent', border: 'none' }}></td>
                                </tr>
                            </tbody>
                        </table>

                        {error && (
                            <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '8px', background: '#fff5f5', border: '1px solid #dc3545', borderRadius: '4px', color: '#dc3545', fontSize: '12px' }}>
                                <span style={{ fontWeight: '600' }}>{error}</span>
                            </div>
                        )}

                        <div data-export-hide="true" style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginTop: '15px', paddingBottom: '10px', background: '#f8f9fa', padding: '15px', borderRadius: '8px', marginLeft: '5px', marginRight: '5px' }}>
                            <button 
                                type="button" 
                                onClick={handleSaveAndApply} 
                                disabled={saving}
                                style={{ 
                                    padding: '10px 25px', 
                                    fontSize: '14px', 
                                    fontWeight: '600', 
                                    background: '#03428d', 
                                    color: 'white', 
                                    border: 'none', 
                                    borderRadius: '8px', 
                                    cursor: 'pointer', 
                                    opacity: saving ? 0.7 : 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                {saving ? (
                                    <>
                                        <div style={{ width: '16px', height: '16px', border: '2px solid #fff', borderTop: '2px solid transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                                        <span>جاري الحفظ...</span>
                                    </>
                                ) : (
                                    <>
                                        <svg style={{ width: '18px', height: '18px' }} fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                        <span>{isNewFile ? 'تطبيق' : (hasExistingData ? 'حفظ وتطبيق' : 'حفظ التكلفة')}</span>
                                    </>
                                )}
                            </button>
                            <button 
                                type="button" 
                                onClick={onClose} 
                                disabled={saving}
                                style={{ 
                                    padding: '10px 25px', 
                                    fontSize: '14px', 
                                    fontWeight: '600', 
                                    background: 'white', 
                                    color: '#333', 
                                    border: '2px solid #ddd', 
                                    borderRadius: '8px', 
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                <svg style={{ width: '18px', height: '18px' }} fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                                <span>إلغاء</span>
                            </button>
                            <button 
                                type="button" 
                                onClick={handleExportImage} 
                                disabled={exporting}
                                style={{ 
                                    padding: '10px 25px', 
                                    fontSize: '14px', 
                                    fontWeight: '600', 
                                    background: 'linear-gradient(135deg, #2d8659 0%, #3da86e 100%)', 
                                    color: 'white', 
                                    border: 'none', 
                                    borderRadius: '8px', 
                                    cursor: 'pointer', 
                                    opacity: exporting ? 0.7 : 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                {exporting ? (
                                    <>
                                        <div style={{ width: '16px', height: '16px', border: '2px solid #fff', borderTop: '2px solid transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                                        <span>جاري التصدير...</span>
                                    </>
                                ) : (
                                    <>
                                        <svg style={{ width: '18px', height: '18px' }} fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M4 5a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V7a2 2 0 00-2-2h-1.586a1 1 0 01-.707-.293l-1.121-1.121A2 2 0 0011.172 3H8.828a2 2 0 00-1.414.586L6.293 4.707A1 1 0 015.586 5H4zm6 9a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                                        </svg>
                                        <span>تصدير صورة</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default React.memo(LandCostModal);
